import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, Locale } from "date-fns";
import { de, enUS } from "date-fns/locale";
import {
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  PlayCircle,
  UserPlus,
  Clock,
} from "lucide-react";
import { DefectComment } from "@/hooks/useDefectComments";
import { DefectWithDetails } from "@/hooks/useDefectsQuery";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { DisplayableProfile } from "@/lib/profileUtils";

interface TimelineEvent {
  id: string;
  type: "created" | "comment" | "resolved" | "cancelled" | "in_progress" | "assigned";
  timestamp: Date;
  userName: string;
  userProfile?: DisplayableProfile | null;
  content?: string;
}

interface DefectTimelineProps {
  defect: DefectWithDetails;
  comments: DefectComment[] | undefined;
  isLoading?: boolean;
}

function getProfileName(
  profile?: {
    first_name?: string | null;
    last_name?: string | null;
    display_name?: string | null;
    email?: string;
  } | null,
): string {
  if (!profile) return "Unbekannt";
  if (profile.display_name) return profile.display_name;
  if (profile.first_name || profile.last_name) {
    return [profile.first_name, profile.last_name].filter(Boolean).join(" ");
  }
  return profile.email || "Unbekannt";
}

function TimelineIcon({ type }: { type: TimelineEvent["type"] }) {
  switch (type) {
    case "created":
      return <AlertTriangle className="h-4 w-4 text-destructive" />;
    case "comment":
      return <MessageSquare className="h-4 w-4 text-primary" />;
    case "resolved":
      return <CheckCircle2 className="h-4 w-4 text-success" />;
    case "cancelled":
      return <XCircle className="h-4 w-4 text-muted-foreground" />;
    case "in_progress":
      return <PlayCircle className="h-4 w-4 text-warning" />;
    case "assigned":
      return <UserPlus className="h-4 w-4 text-info" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground" />;
  }
}

function TimelineItem({ event, dateLocale }: { event: TimelineEvent; dateLocale: Locale }) {
  const { t } = useTranslation();

  const getEventLabel = () => {
    switch (event.type) {
      case "created":
        return t("defects.timeline.created");
      case "comment":
        return t("defects.timeline.commented");
      case "resolved":
        return t("defects.timeline.resolved");
      case "cancelled":
        return t("defects.timeline.cancelled");
      case "in_progress":
        return t("defects.timeline.inProgress");
      case "assigned":
        return t("defects.timeline.assigned");
      default:
        return "";
    }
  };

  return (
    <div className="flex gap-3 pb-4 last:pb-0">
      <div className="flex flex-col items-center">
        <div className="flex h-8 w-8 items-center justify-center rounded-full border bg-background">
          {event.type === "comment" && event.userProfile ? (
            <ProfileAvatar profile={event.userProfile} size="sm" />
          ) : (
            <TimelineIcon type={event.type} />
          )}
        </div>
        <div className="flex-1 w-px bg-border mt-2 last:hidden" />
      </div>
      <div className="flex-1 pt-1 pb-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-sm">{event.userName}</span>
          <span className="text-xs text-muted-foreground">{getEventLabel()}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {format(event.timestamp, "d. MMM yyyy, HH:mm", { locale: dateLocale })}
        </p>
        {event.content && (
          <div className="mt-2 p-2 rounded-md bg-muted/50 text-sm">{event.content}</div>
        )}
      </div>
    </div>
  );
}

export function DefectTimeline({ defect, comments, isLoading }: DefectTimelineProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : de;

  const events = useMemo(() => {
    const allEvents: TimelineEvent[] = [];

    // Creation event
    allEvents.push({
      id: "created",
      type: "created",
      timestamp: new Date(defect.created_at),
      userName: getProfileName(defect.created_by_profile),
    });

    // Comments
    comments?.forEach((comment) => {
      allEvents.push({
        id: `comment-${comment.id}`,
        type: "comment",
        timestamp: new Date(comment.created_at),
        userName: getProfileName(comment.user_profile),
        userProfile: comment.user_profile,
        content: comment.content,
      });
    });

    // Resolution event
    if (defect.resolved_at) {
      allEvents.push({
        id: "resolved",
        type: "resolved",
        timestamp: new Date(defect.resolved_at),
        userName: getProfileName(defect.assigned_to_profile), // Person who resolved
        content: defect.resolution_notes || undefined,
      });
    }

    // Cancellation event
    if (defect.cancelled_at) {
      allEvents.push({
        id: "cancelled",
        type: "cancelled",
        timestamp: new Date(defect.cancelled_at),
        userName: getProfileName(defect.created_by_profile), // Assuming creator cancels
        content: defect.cancellation_reason || undefined,
      });
    }

    // Sort by timestamp
    return allEvents.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }, [defect, comments]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium">{t("defects.timeline.title")}</h4>
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium">{t("defects.timeline.title")}</h4>
      <ScrollArea className="max-h-[300px] pr-4">
        <div className="space-y-0">
          {events.map((event) => (
            <TimelineItem key={event.id} event={event} dateLocale={dateLocale} />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
